"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCheckOutline18, IconChevronExpandYOutline18 } from "nucleo-ui-outline-18";
import { Badge } from "@/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type { RecentChain } from "@/lib/utils/recentSelections";
import type { Chain } from "@/lib/utils/chainSelection";
import * as React from "react";

/**
 * Pure presentational view for ChainSelector. All chain data, recent-chain
 * lookups, and search-filtering are passed in as props — this component
 * never calls a data hook directly. The outer ChainSelector still owns the
 * search hook + selected-chain fetch + persistent recent-chain mutation;
 * this layer just paints them.
 */
export interface ChainSelectorViewProps {
  /** Visible label rendered above the trigger button. */
  label: string;
  /** Optional helper text under the label. */
  description?: string;
  /** Slot rendered to the right of the label (e.g. variable picker). */
  headerRight?: React.ReactNode;
  /** Currently selected chain — drives the trigger button content. */
  selectedChain: Chain | null;
  /** All searchable chains in the order they should appear in the list. */
  orderedChains: Chain[];
  /** Recently picked chains (top of the list when no search query is active). */
  recentChains: RecentChain[];
  /** True while the chain catalog is being loaded for the first time. */
  isLoading: boolean;
  /** Current search query. The view is fully controlled — caller filters `orderedChains`. */
  searchQuery: string;
  onSearchChange: (query: string) => void;
  /** Whether the popover is open. Controlled by the outer component. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Active chain id (or undefined). Used to render the check icon next to rows. */
  numericValue: number | undefined;
  /** Handler invoked when the user picks a chain or recent chain. */
  onSelect: (chain: Chain | RecentChain) => void;
  /** Whether the testnet toggle should be rendered next to the label. */
  showTestnetToggle: boolean;
  /** Current testnet toggle state. */
  testnetFilter: boolean;
  onTestnetFilterChange: (next: boolean) => void;
  /** When true, Popover uses modal mode (required inside dialogs). */
  popoverModal?: boolean;
  /** Disable the trigger entirely — used for read-only or external-loading states. */
  disabled?: boolean;
  /** Optional placeholder for empty state. Defaults to "Select chain...". */
  placeholder?: string;
}
export function ChainSelectorView(t0) {
  const $ = _c(47);
  const {
    label,
    description,
    headerRight,
    selectedChain,
    orderedChains,
    recentChains,
    isLoading,
    searchQuery,
    onSearchChange,
    open,
    onOpenChange,
    numericValue,
    onSelect,
    showTestnetToggle,
    testnetFilter,
    onTestnetFilterChange,
    popoverModal: t1,
    disabled: t2,
    placeholder: t3
  } = t0;
  const popoverModal = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const placeholder = t3 === undefined ? "Select chain..." : t3;
  const listboxId = React.useId();
  const [containerEl, setContainerEl] = React.useState(null);
  const searchInputRef = React.useRef(null);
  let t4;
  if ($[0] !== headerRight || $[1] !== label || $[2] !== onTestnetFilterChange || $[3] !== showTestnetToggle || $[4] !== testnetFilter) {
    t4 = label && <div className="flex items-center justify-between"><label className="field-label block">{label}</label><div className="flex items-center gap-3">{showTestnetToggle && <div className="flex items-center gap-2"><span className="micro-label">Testnets</span><Switch size="sm" checked={testnetFilter === true} onCheckedChange={checked => {
            onTestnetFilterChange(checked ? true : false);
          }} /></div>}{headerRight}</div></div>;
    $[0] = headerRight;
    $[1] = label;
    $[2] = onTestnetFilterChange;
    $[3] = showTestnetToggle;
    $[4] = testnetFilter;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== description) {
    t5 = description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>;
    $[6] = description;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== placeholder || $[9] !== selectedChain) {
    t6 = selectedChain ? <span className="flex items-center gap-2">{selectedChain.iconUrl && <img src={selectedChain.iconUrl} alt={selectedChain.name} className="h-4 w-4 rounded-full" />}<span className="text-ds-text-primary">{selectedChain.name}</span>{selectedChain.isTestnet && <Badge>Testnet</Badge>}</span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">{placeholder}</span>;
    $[8] = placeholder;
    $[9] = selectedChain;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconChevronExpandYOutline18 className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== disabled || $[13] !== listboxId || $[14] !== open || $[15] !== t6) {
    t8 = <PopoverTrigger asChild={true}><button type="button" role="combobox" aria-controls={listboxId} aria-expanded={open} disabled={disabled} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60">{t6}{t7}</button></PopoverTrigger>;
    $[12] = disabled;
    $[13] = listboxId;
    $[14] = open;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = popoverModal ? containerEl ?? undefined : undefined;
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e => {
      e.preventDefault();
      searchInputRef.current?.focus({
        preventScroll: true
      });
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== onSearchChange || $[19] !== searchQuery) {
    t11 = <CommandInput ref={searchInputRef} placeholder="Search chains..." value={searchQuery} onValueChange={onSearchChange} />;
    $[18] = onSearchChange;
    $[19] = searchQuery;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== isLoading || $[22] !== numericValue || $[23] !== onSelect || $[24] !== orderedChains || $[25] !== recentChains || $[26] !== searchQuery) {
    t12 = isLoading ? <div className="body-sm py-6 text-center">Loading...</div> : <><CommandEmpty>No chain found.</CommandEmpty>{recentChains.length > 0 && !searchQuery && <CommandGroup heading="Recently Used">{recentChains.map(chain => <CommandItem key={`recent-${chain.chainId}`} value={chain.chainId.toString()} onSelect={() => onSelect(chain)} className="hover:bg-blue-50 dark:hover:bg-blue-950/20"><div className="flex w-full cursor-pointer items-center gap-2"><IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", numericValue === chain.chainId ? "opacity-100" : "opacity-0")} />{chain.iconUrl && <img src={chain.iconUrl} alt={chain.name} className="h-4 w-4 rounded-full" />}<span className="flex-1 truncate">{chain.name}</span></div></CommandItem>)}</CommandGroup>}<CommandGroup heading={recentChains.length > 0 && !searchQuery ? "All Chains" : undefined}>{orderedChains.map(chain_0 => <CommandItem key={chain_0.chainId} value={chain_0.chainId.toString()} onSelect={() => onSelect(chain_0)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800"><div className="flex w-full cursor-pointer items-center gap-2"><IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", numericValue === chain_0.chainId ? "opacity-100" : "opacity-0")} />{chain_0.iconUrl && <img src={chain_0.iconUrl} alt={chain_0.name} className="h-4 w-4 flex-shrink-0 rounded-full" />}<span className="flex-1 truncate">{chain_0.name}</span>{chain_0.isTestnet && <Badge className="flex-shrink-0">Testnet</Badge>}</div></CommandItem>)}</CommandGroup></>;
    $[21] = isLoading;
    $[22] = numericValue;
    $[23] = onSelect;
    $[24] = orderedChains;
    $[25] = recentChains;
    $[26] = searchQuery;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== listboxId || $[29] !== t12) {
    t13 = <CommandList id={listboxId} className="max-h-64">{t12}</CommandList>;
    $[28] = listboxId;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11 || $[32] !== t13) {
    t14 = <Command shouldFilter={false}>{t11}{t13}</Command>;
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14 || $[35] !== t9) {
    t15 = <PopoverContent className="bg-ds-surface-card w-[var(--radix-popover-trigger-width)] p-0" align="start" container={t9} onOpenAutoFocus={t10}>{t14}</PopoverContent>;
    $[34] = t14;
    $[35] = t9;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== onOpenChange || $[38] !== open || $[39] !== popoverModal || $[40] !== t15 || $[41] !== t8) {
    t16 = <Popover open={open} onOpenChange={onOpenChange} modal={popoverModal}>{t8}{t15}</Popover>;
    $[37] = onOpenChange;
    $[38] = open;
    $[39] = popoverModal;
    $[40] = t15;
    $[41] = t8;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t16 || $[44] !== t4 || $[45] !== t5) {
    t17 = <div ref={setContainerEl} className="space-y-2">{t4}{t5}{t16}</div>;
    $[43] = t16;
    $[44] = t4;
    $[45] = t5;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
}
export default ChainSelectorView;